import { normalizeUsPhoneForApi } from '@act/storybook';

/**
 * Place Details fields requested on cache miss (camelCase per Places API New).
 *
 * @see https://developers.google.com/maps/documentation/places/web-service/data-fields
 */
export const PLACE_DETAILS_FETCH_FIELDS = [
  'id',
  'displayName',
  'formattedAddress',
  'location',
  'types',
  'websiteURI',
  'nationalPhoneNumber',
] as const;

/**
 * Reads display text from a Places (New) `displayName` field (string or localized object).
 *
 * @param displayName - Value returned on the `Place` instance after `fetchFields`.
 * @returns Plain text for UI and summaries.
 */
export function extractPlaceDisplayName(
  displayName: string | { text: string } | undefined
): string {
  if (typeof displayName === 'string') {
    return displayName;
  }
  if (displayName && typeof displayName === 'object' && 'text' in displayName) {
    const { text } = displayName;
    return typeof text === 'string' ? text : '';
  }
  return '';
}

/**
 * Maps Places `nationalPhoneNumber` into form `phoneNumber` (persisted as `cbo_record.phone_number`).
 *
 * @param nationalPhoneNumber - `nationalPhoneNumber` from Place Details Enterprise.
 * @returns Stored phone string for the masked phone input, or empty when unavailable.
 */
export function extractPlacePhoneForForm(nationalPhoneNumber?: string): string {
  const raw = nationalPhoneNumber?.trim() ?? '';
  if (!raw) {
    return '';
  }
  return normalizeUsPhoneForApi(raw) ?? raw;
}

/**
 * Reads website URI from Place Details for the CBO form.
 *
 * @param websiteURI - `websiteURI` from Place Details (JS SDK property name).
 * @returns Trimmed URL string for `websiteUrl`, or empty when absent.
 */
export function extractPlaceWebsiteForForm(websiteURI?: string): string {
  return websiteURI?.trim() ?? '';
}
